import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import {
  Box as ChakraUiBox,
  Image as ChakraUiImage,
  ImageProps,
  AspectRatio as ChakraUiAspectRatio,
} from '@chakra-ui/react';
import { useSvgContent } from './use-svg-content';

interface Props {
  align?: 'center' | 'left' | 'right';
  aspectRatio?: number;
  description?: string;
  height?: string;
  id?: string;
  path: string;
  sizes?: string;
  srcSet?: string;
  width?: string;
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  fallbackSrc?: string;
  imageProps?: ImageProps;
}

export const Image: FC<Props> = ({
  align = 'center',
  aspectRatio,
  description,
  fallbackSrc,
  height = 'auto',
  id,
  path,
  sizes,
  srcSet,
  width = '100%',
  objectFit,
  imageProps = {},
  ...rest
}) => {
  const { shouldInlineSvg, svgContent, svgError } = useSvgContent(path);
  const {
    src: _imageSrc,
    alt: _imageAlt,
    srcSet: _imageSrcSet,
    sizes: _imageSizes,
    fit: _imageFit,
    objectFit: _imageObjectFit,
    loading: _imageLoading,
    onError: imageOnError,
    css: svgContainerCss,
    ...svgContainerProps
  } = imageProps as ImageProps;

  const isSvgLoading = shouldInlineSvg && !svgError && !svgContent;
  const inlineSvgObjectFit =
    objectFit ?? _imageObjectFit ?? (svgContainerProps.style as React.CSSProperties | undefined)?.objectFit;

  // Chakra's Image has no fallbackSrc, fallback or onError handling (no `useImage` preload, no swap),
  // so a plain onError swap to the fallback src stands in.
  const [hasErrored, setHasErrored] = useState(false);
  useEffect(() => {
    setHasErrored(false);
  }, [path, fallbackSrc]);
  const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    imageOnError?.(event);
    if (fallbackSrc) setHasErrored(true);
  };

  const props: ImageProps = {
    id,
    src: path,
    alt: description,
    sizes,
    srcSet,
    // Chakra's Image has no recipe (a bare `chakra.img` defaulting `fit` to `cover`), so the contain/centre
    // defaults live here, still overridable by an explicit prop or by `imageProps`.
    objectFit: objectFit ?? 'contain',
    objectPosition: align === 'center' ? 'center center' : align,
    ...imageProps,
    ...(fallbackSrc ? { src: hasErrored ? fallbackSrc : (imageProps.src ?? path), onError: handleImageError } : {}),
    // A srcset candidate outranks src, so the fallback only shows once srcset is gone.
    ...(fallbackSrc && hasErrored ? { srcSet: undefined, sizes: undefined } : {}),
  };

  const renderImage = () => {
    if (shouldInlineSvg && !svgError) {
      return (
        <ChakraUiBox
          id={id}
          data-object-type="core-image"
          role={description ? 'img' : undefined}
          aria-label={description}
          aria-hidden={!description}
          {...(aspectRatio ? {} : { width, height })}
          css={{
            ...(inlineSvgObjectFit
              ? {
                  '& > svg': {
                    width: '100%',
                    height: '100%',
                    objectFit: inlineSvgObjectFit,
                  },
                }
              : {}),
            ...(svgContainerCss as object),
          }}
          {...svgContainerProps}
          dangerouslySetInnerHTML={{
            __html: svgContent,
          }}
        />
      );
    }

    return (
      <ChakraUiImage
        data-object-type="core-image"
        {...props}
        {...(aspectRatio ? {} : { htmlWidth: width, htmlHeight: height, ...rest })}
      />
    );
  };

  // While the SVG fetch is in-flight, render nothing only when aspectRatio is
  // not set to avoid duplicate <img> requests + flicker.
  if (isSvgLoading && !aspectRatio) return null;

  if (aspectRatio) {
    return (
      <ChakraUiAspectRatio data-object-type="core-aspect-ratio" ratio={aspectRatio} w={width} h={height}>
        {isSvgLoading ? <ChakraUiBox data-object-type="core-image" aria-hidden={true} /> : renderImage()}
      </ChakraUiAspectRatio>
    );
  }

  return renderImage();
};
